import React, { useState, useEffect } from 'react';
import { HiPencil, HiTrash, HiCheck, HiX, HiClock, HiMail, HiSearch } from 'react-icons/hi';
import './styles/VisiteursManagement.css';
import { visiteursService } from '../../services/visiteursService';
import { conferencesService } from '../../services/conferencesService';
import { RiFileExcel2Line } from "react-icons/ri";
import * as XLSX from 'xlsx';
import emailjs from '@emailjs/browser';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import ExpandableText from './ExpandableText';

function VisiteursManagement({ selectedEdition }) {
  const [visiteurs, setVisiteurs] = useState([]);
  const [conferences, setConferences] = useState([]);
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [filtreConference, setFiltreConference] = useState('toutes');
  const [searchTerm, setSearchTerm] = useState(''); // <-- NEW: Global search state
  const [editingVisiteur, setEditingVisiteur] = useState(null);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  const statutOptions = [
    { value: 'en_attente', label: 'En attente', color: '#f59e0b', icon: <HiClock /> },
    { value: 'valide', label: 'Validé', color: '#10b981', icon: <HiCheck /> },
    { value: 'rejete', label: 'Rejeté', color: '#ef4444', icon: <HiX /> },
    { value: 'present', label: 'Présent', color: '#8b5cf6', icon: <HiCheck /> },
    { value: 'email confirme', label: 'Email confirmé', color: '#2563eb', icon: <HiMail /> },
    { value: 'email_envoye', label: 'Email envoyé', color: '#06b6d4', icon: <HiMail /> }
  ];

  const profilLabels = {
    'student': 'Étudiant',
    'professor': 'Professeur',
    'professional': 'Professionnel'
  };

  useEffect(() => {
    if (selectedEdition) {
      // Immediate data refresh only
      loadData();

      // Remove polling - no more automatic refresh
      // Real-time updates will happen on user actions
    }
  }, [selectedEdition]);

  const loadData = async () => {
    if (!selectedEdition) return;

    try {
      setLoading(true);
      console.log('Loading visiteurs for edition:', selectedEdition);

      // Load visiteurs and conferences in parallel
      const [visiteursData, conferencesData] = await Promise.all([
        visiteursService.getVisiteurs(selectedEdition),
        conferencesService.getConferencesData(selectedEdition)
      ]);

      // Filter out conferencier principal as they have their own management page
      const regularVisiteurs = visiteursData.filter(v =>
        v.typeParticipation !== 'conferencier_principal' && v.categorie !== 'conferencier'
      );

      setVisiteurs(regularVisiteurs);
      setConferences(conferencesData.conferences || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setVisiteurs([]);
      setConferences([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatut = async (id, nouveauStatut) => {
    try {
      await visiteursService.updateVisiteurStatus(id, nouveauStatut);

      // Update state immediately for instant refresh
      setVisiteurs(prev => prev.map(visiteur =>
        visiteur.id === id
          ? {
            ...visiteur,
            statut: nouveauStatut,
            // Add reference if status changed to 'valide' and no reference exists
            referenceParticipation: (nouveauStatut === 'valide' && !visiteur.referenceParticipation)
              ? `GSIDA${Date.now()}`
              : visiteur.referenceParticipation
          }
          : visiteur
      ));

      // Send welcome email automatically when status is set to "present"
      if (nouveauStatut === 'present') {
        await handleSendWelcomeEmail(id);
      }

      showSuccessMessage();

      // No refresh needed - state already updated
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Erreur lors de la mise à jour du statut.');
    }
  };

  const handleDeleteVisiteur = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette inscription ?')) {
      try {
        await visiteursService.deleteVisiteur(id);

        // Update state immediately for instant refresh
        setVisiteurs(prev => prev.filter(visiteur => visiteur.id !== id));
        showSuccessMessage();

        // No refresh needed - state already updated
      } catch (error) {
        console.error('Error deleting visiteur:', error);
        alert('Erreur lors de la suppression.');
      }
    }
  };

  const handleEditVisiteur = (visiteur) => {
    setEditingVisiteur(visiteur);
  };

  const handleSaveVisiteur = async (updatedVisiteur) => {
    try {
      await visiteursService.updateVisiteur(updatedVisiteur.id, updatedVisiteur);

      // Update local state immediately
      setVisiteurs(prev => prev.map(visiteur =>
        visiteur.id === updatedVisiteur.id
          ? {
            ...visiteur,
            ...updatedVisiteur,
            conferenceTitre: getConferenceName(updatedVisiteur.conference)
          }
          : visiteur
      ));

      setEditingVisiteur(null);
      showSuccessMessage();

      // No refresh needed - state already updated
    } catch (error) {
      console.error('Error updating visiteur:', error);
      alert('Erreur lors de la modification.');
    }
  };

  const getVisiteursFiltres = () => {
    let filtered = visiteurs;

    // Filter by status
    if (filtreStatut !== 'tous') {
      filtered = filtered.filter(visiteur => visiteur.statut === filtreStatut);
    }

    // Filter by conference - Fix the comparison logic
    if (filtreConference !== 'toutes') {
      filtered = filtered.filter(visiteur => {
        // Convert both to strings and compare
        const visiteurConference = String(visiteur.conference || '');
        const selectedConference = String(filtreConference);
        return visiteurConference === selectedConference;
      });
    }

    // Global search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(visiteur =>
        `${visiteur.prenom} ${visiteur.nom}`.toLowerCase().includes(searchLower) ||
        visiteur.email.toLowerCase().includes(searchLower) ||
        visiteur.telephone.includes(searchTerm) ||
        (visiteur.organisation || '').toLowerCase().includes(searchLower) ||
        (visiteur.referenceParticipation || '').toLowerCase().includes(searchLower) ||
        (visiteur.conferenceTitre || '').toLowerCase().includes(searchLower) ||
        (visiteur.jour_visiteur || '').toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  };

  const getStatutInfo = (statut) => {
    return statutOptions.find(option => option.value === statut);
  };

  const getConferenceName = (conferenceId) => {
    const conference = conferences.find(c => c.id.toString() === conferenceId.toString());
    return conference ? conference.titre : `Conférence ID: ${conferenceId}`;
  };

  const getStatistiques = () => {
    const total = visiteurs.length;
    const enAttente = visiteurs.filter(v => v.statut === 'en_attente').length;
    const valides = visiteurs.filter(v => v.statut === 'valide').length;
    const rejetes = visiteurs.filter(v => v.statut === 'rejete').length;
    const presents = visiteurs.filter(v => v.statut === 'present').length;
    const emailConfirmes = visiteurs.filter(v => v.statut === 'email confirme').length;
    const emailEnvoyes = visiteurs.filter(v => v.statut === 'email_envoye').length;

    return { total, enAttente, valides, rejetes, presents, emailConfirmes, emailEnvoyes };
  };

  const handleSendTicket = async (visiteurId) => {
    try {
      const visiteur = visiteurs.find(v => v.id === visiteurId);
      if (!visiteur) return alert("Visiteur introuvable.");

      // Defensive: fallback to empty string if any field is missing
      const to_email = visiteur.email || '';
      const to_name = `${visiteur.prenom || ''} ${visiteur.nom || ''}`.trim();
      const short_name = `${visiteur.prenom || ''} ${visiteur.nom || ''}`.trim();
      const reference = visiteur.referenceParticipation || '';

      // Use QR code service URL instead of base64
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(reference)}`;

      const confirmLink = `${window.location.origin}/#confirm?ref=${encodeURIComponent(reference)}`;

      // Conference info
      const conference = conferences.find(c => c.id.toString() === visiteur.conference?.toString());
      const conference_titre = conference ? conference.titre : '';
      const conference_date = conference ? conference.date : '';
      const conference_salle = conference ? conference.salle : '';

      // Check for missing required fields
      if (!to_email || !to_name || !reference) {
        alert('Impossible d\'envoyer l\'email : données manquantes.');
        return;
      }

      const emailVars = {
        to_email,
        to_name,
        short_name,
        reference,
        qr_code_url: qrCodeUrl,
        confirm_link: confirmLink,
        conference_titre,
        conference_date,
        conference_salle,
      };

      await emailjs.send(
        'service_i9giahi',
        'template_vo95vrq',
        emailVars,
        '_UeBfJMtIUI_gP-mW'
      );

      // Update status to 'email_envoye' and mark ticket as sent
      await visiteursService.updateVisiteurStatus(visiteurId, 'email_envoye');
      await visiteursService.markTicketSent(visiteurId);

      // Update state immediately for instant refresh
      setVisiteurs(prev => prev.map(v =>
        v.id === visiteurId
          ? { ...v, ticketSent: true, statut: 'email_envoye' }
          : v
      ));
      alert('Ticket envoyé avec succès ! Statut mis à jour vers "Email envoyé".');
      showSuccessMessage();

      // No refresh needed - state already updated
    } catch (error) {
      console.error('Error sending ticket:', error);
      alert('Erreur lors de l\'envoi du ticket.');
    }
  };

  const handleCreateReference = async (visiteurId) => {
    try {
      const updatedVisiteur = await visiteursService.createParticipationReference(visiteurId);

      // Update local state immediately
      setVisiteurs(prev => prev.map(visiteur =>
        visiteur.id === visiteurId
          ? { ...visiteur, referenceParticipation: updatedVisiteur.reference_participation }
          : visiteur
      ));

      showSuccessMessage();

      // No refresh needed - state already updated
    } catch (error) {
      console.error('Error creating reference:', error);
      alert('Erreur lors de la création de la référence.');
    }
  };

  const showSuccessMessage = () => {
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  // Export filtered data to Excel
  const handleExportExcel = () => {
    const filtered = getVisiteursFiltres();
    // Prepare data for Excel
    const data = filtered.map(v => ({
      Nom: `${v.prenom} ${v.nom}`,
      Email: v.email,
      Téléphone: v.telephone,
      Organisation: v.organisation || 'Non renseigné',
      Profil: profilLabels[v.profil] || v.profil,
      Conférence: v.conferenceTitre,
      'Jours de visite': v.jour_visiteur || 'Non spécifié',
      'Date inscription': v.dateInscription,
      Référence: v.referenceParticipation || '',
      Statut: getStatutInfo(v.statut)?.label || v.statut,
      'Ticket envoyé': v.ticketSent ? 'Oui' : 'Non'
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Visiteurs');
    XLSX.writeFile(wb, 'visiteurs.xlsx');
  };

  // Placeholder for Google Sheets real-time sync
  // This requires backend integration (Node.js/Apps Script) to push updates to Google Sheets.
  // You can trigger a webhook or use Google Sheets API from your backend when data changes.
  // Example:
  // useEffect(() => {
  //   // Call backend endpoint to sync with Google Sheets whenever visiteurs changes
  //   // fetch('/api/sync-google-sheet', { method: 'POST', body: JSON.stringify(visiteurs) });
  // }, [visiteurs]);

  const handleSendWelcomeEmail = async (visiteurId) => {
    try {
      const visiteur = visiteurs.find(v => v.id === visiteurId);
      if (!visiteur) return;

      const to_email = visiteur.email || '';
      const to_name = `${visiteur.prenom || ''} ${visiteur.nom || ''}`.trim();
      const conference = conferences.find(c => c.id.toString() === visiteur.conference?.toString());
      const conference_titre = conference ? conference.titre : '';
      const conference_date = conference ? conference.date : '';
      const conference_salle = conference ? conference.salle : '';

      const emailVars = {
        to_email,
        to_name,
        conference_titre,
        conference_date,
        conference_salle,
      };

      await emailjs.send(
        'service_i9giahi',
        'template_2tcwp6f', // <-- Replace with your actual EmailJS welcome template ID
        emailVars,
        '_UeBfJMtIUI_gP-mW'
      );
      // Optionally, you can set a flag like welcomeSent in state if you want to prevent duplicates
    } catch (error) {
      console.error('Error sending welcome email:', error);
    }
  };

  // Helper: get salle capacity by salleId
  const getSalleCapacity = (salleId) => {
    const salle = conferences.find(s => String(s.id) === String(salleId));
    return salle ? salle.capacite || 0 : 0;
  };

  // Helper: count confirmed participants for a conference
  const getConfirmedParticipantsCount = (conferenceId) => {
    return visiteurs.filter(v => v.conference === conferenceId && v.statut === 'email confirme').length;
  };

  if (!selectedEdition) {
    return <div className="admin-section">Please select an edition first</div>;
  }

  if (loading) {
    return <div className="admin-section">Loading...</div>;
  }

  const stats = getStatistiques();

  return (
    <div className="admin-section">
      {/* Success Message */}
      {showSaveSuccess && (
        <div className="save-success-message">
          <HiCheck /> Opération effectuée avec succès !
        </div>
      )}

      {/* Statistiques */}
      <div className="section-header">
        <h3>Statistiques des Inscriptions</h3>
        {/* Export Excel Button */}
        <button
          onClick={handleExportExcel}
          className="btn-primary"
          style={{
            marginLeft: 'auto',
            fontSize: 'clamp(0.7rem, 1.8vw, 0.9rem)',
            padding: '0.5em 1.5em',
            cursor: 'pointer',
            backgroundColor: '#107c41',
          }}
        >
          <RiFileExcel2Line size={20} />
          Exporter Excel
        </button>
      </div>
      <div className="stats-grid">
        <div className="stat-card">
          <h4>Total</h4>
          <span className="stat-number">{stats.total}</span>
        </div>
        <div className="stat-card">
          <h4>En attente</h4>
          <span className="stat-number" style={{ color: '#f59e0b' }}>{stats.enAttente}</span>
        </div>
        <div className="stat-card">
          <h4>Validés</h4>
          <span className="stat-number" style={{ color: '#10b981' }}>{stats.valides}</span>
        </div>
        <div className="stat-card">
          <h4>Rejetés</h4>
          <span className="stat-number" style={{ color: '#ef4444' }}>{stats.rejetes}</span>
        </div>
        <div className="stat-card">
          <h4>Présents</h4>
          <span className="stat-number" style={{ color: '#8b5cf6' }}>{stats.presents}</span>
        </div>
        <div className="stat-card">
          <h4>Email Confirmés</h4>
          <span className="stat-number" style={{ color: '#2563eb' }}>{stats.emailConfirmes}</span>
        </div>
        <div className="stat-card">
          <h4>Email Envoyés</h4>
          <span className="stat-number" style={{ color: '#06b6d4' }}>{stats.emailEnvoyes}</span>
        </div>
      </div>

      {/* Global Search and Filters */}
      <div className="visitors-management-header">
        <div className="header-title">
          <h3>Liste des Visiteurs Inscrits</h3>
          <span className="visitors-count">{getVisiteursFiltres().length} visiteur(s)</span>
        </div>

        <div className="search-filters-container">
          {/* Global Search */}
          <div className="search-section">
            <div className="search-wrapper">
              <HiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Rechercher par nom, email, téléphone, organisation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="clear-search-btn"
                  aria-label="Effacer la recherche"
                >
                  <HiX />
                </button>
              )}
            </div>
          </div>

          {/* Filters Section */}
          <div className="filters-section">
            <div className="filter-group">
              <label className="filter-label">Statut</label>
              <select
                value={filtreStatut}
                onChange={(e) => setFiltreStatut(e.target.value)}
                className="filter-select"
              >
                <option value="tous">Tous les statuts</option>
                <option value="en_attente">En attente</option>
                <option value="valide">Validés</option>
                <option value="rejete">Rejetés</option>
                <option value="present">Présents</option>
                <option value="email confirme">Email Confirmés</option>
                <option value="email_envoye">Email Envoyés</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Conférence</label>
              <select
                value={filtreConference}
                onChange={e => setFiltreConference(e.target.value)}
                className="filter-select"
              >
                <option value="toutes">Toutes les conférences</option>
                {conferences.map(conf => {
                  const salleCapacity = conf.salleId ? getSalleCapacity(conf.salleId) : 0;
                  const confirmedCount = getConfirmedParticipantsCount(conf.id);
                  const isFull = salleCapacity > 0 && confirmedCount >= salleCapacity;
                  return (
                    <option
                      key={conf.id}
                      value={conf.id.toString()}
                      disabled={isFull}
                      className={isFull ? 'option-disabled' : ''}
                    >
                      {conf.titre} {isFull ? '(Complet)' : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Reset Filters Button */}
            {(filtreStatut !== 'tous' || filtreConference !== 'toutes' || searchTerm) && (
              <button
                onClick={() => {
                  setFiltreStatut('tous');
                  setFiltreConference('toutes');
                  setSearchTerm('');
                }}
                className="reset-filters-btn"
                title="Réinitialiser tous les filtres"
              >
                Réinitialiser
              </button>
            )}
          </div>
        </div>

        {/* Active Filters Display */}
        {(filtreStatut !== 'tous' || filtreConference !== 'toutes' || searchTerm) && (
          <div className="active-filters">
            <span className="active-filters-label">Filtres actifs:</span>
            <div className="active-filters-list">
              {searchTerm && (
                <span className="filter-tag">
                  Recherche: "{searchTerm}"
                  <button onClick={() => setSearchTerm('')} className="remove-filter">
                    <HiX />
                  </button>
                </span>
              )}
              {filtreStatut !== 'tous' && (
                <span className="filter-tag">
                  Statut: {statutOptions.find(s => s.value === filtreStatut)?.label}
                  <button onClick={() => setFiltreStatut('tous')} className="remove-filter">
                    <HiX />
                  </button>
                </span>
              )}
              {filtreConference !== 'toutes' && (
                <span className="filter-tag">
                  Conférence: {conferences.find(c => c.id.toString() === filtreConference)?.titre}
                  <button onClick={() => setFiltreConference('toutes')} className="remove-filter">
                    <HiX />
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <DataTable 
          value={getVisiteursFiltres()} 
          paginator 
          rows={20}
          rowsPerPageOptions={[20, 50, 100]}
          className="p-datatable-sm"
          stripedRows
          showGridlines
          responsiveLayout="scroll"
          scrollable
          tableStyle={{ tableLayout: 'auto' }}
          emptyMessage="Aucun visiteur trouvé."
          header={
            <div className="flex justify-content-between align-items-center py-2">
              <span className="p-input-icon-left w-full sm:w-20rem">
                <i className="pi pi-search" style={{ left: '1rem' }} />
                <InputText 
                  type="search" 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                  placeholder="Rechercher par nom, email, organisation..." 
                  className="w-full"
                  style={{ paddingLeft: '2.5rem' }}
                />
              </span>
            </div>
          }
        >
          <Column 
            header="Nom" 
            body={(rowData) => `${rowData.prenom} ${rowData.nom}`} 
            sortable 
            style={{ whiteSpace: 'nowrap' }}
          />
          <Column field="email" header="Email" sortable style={{ whiteSpace: 'nowrap' }} />
          <Column field="telephone" header="Téléphone" style={{ whiteSpace: 'nowrap' }} />
          <Column 
            field="organisation" 
            header="Organisation" 
            sortable 
            body={(rowData) => <ExpandableText text={rowData.organisation || 'Non renseigné'} maxChars={40} />}
            style={{ minWidth: '150px' }}
          />
          <Column field="paye" header="Pays" sortable style={{ width: '1%', whiteSpace: 'nowrap' }} />
          <Column field="ville" header="Ville" sortable style={{ width: '1%', whiteSpace: 'nowrap' }} />
          <Column field="categorie" header="Catégorie" sortable style={{ width: '1%', whiteSpace: 'nowrap' }} />
          <Column 
            field="profil" 
            header="Profil" 
            sortable 
            body={(rowData) => profilLabels[rowData.profil] || rowData.profil}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            field="conferenceTitre" 
            header="Conférence" 
            sortable 
            style={{ minWidth: '200px' }}
            body={(rowData) => <div style={{ fontSize: '0.9em', lineHeight: '1.2' }}>{rowData.conferenceTitre}</div>}
          />
          <Column 
            field="jour_visiteur" 
            header="Jours de visite" 
            sortable 
            style={{ minWidth: '180px' }}
            body={(rowData) => <div style={{ fontSize: '0.9em', lineHeight: '1.2' }}>{rowData.jour_visiteur || '-'}</div>}
          />
          <Column 
            field="dateInscription" 
            header="Date Inscription" 
            sortable 
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            field="referenceParticipation" 
            header="Référence" 
            body={(rowData) => (
              <div className="flex flex-column gap-1">
                {rowData.referenceParticipation ? (
                  <span className="reference-code" style={{ fontSize: '11px' }}>{rowData.referenceParticipation}</span>
                ) : (
                  <Button 
                    label="Générer" 
                    onClick={() => handleCreateReference(rowData.id)}
                    className="p-button-secondary p-button-xs p-1"
                    style={{ fontSize: '10px' }}
                  />
                )}
              </div>
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            field="statut" 
            header="Statut" 
            body={(rowData) => (
              <div className="flex flex-column gap-2 py-2">
                <Tag 
                  value={getStatutInfo(rowData.statut)?.label || rowData.statut} 
                  severity={
                    rowData.statut === 'valide' ? 'success' : 
                    rowData.statut === 'rejete' ? 'danger' : 
                    rowData.statut === 'en_attente' ? 'warning' : 'info'
                  }
                  style={{ width: 'fit-content' }}
                />
                <select
                  value={rowData.statut}
                  onChange={(e) => handleChangeStatut(rowData.id, e.target.value)}
                  className="statut-select"
                  style={{ fontSize: '11px', padding: '2px' }}
                >
                  {statutOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            field="informations" 
            header="Infos/Notes" 
            body={(rowData) => <ExpandableText text={rowData.informations || ''} maxChars={30} />}
            style={{ minWidth: '150px' }}
          />
          <Column 
            header="Actions" 
            body={(rowData) => (
              <div className="flex gap-2">
                <Button 
                  icon="pi pi-pencil" 
                  rounded 
                  outlined 
                  severity="info" 
                  onClick={() => handleEditVisiteur(rowData)} 
                  size="small"
                />
                <Button 
                  icon="pi pi-trash" 
                  rounded 
                  outlined 
                  severity="danger" 
                  onClick={() => handleDeleteVisiteur(rowData.id)} 
                  size="small"
                />
                {rowData.statut === 'valide' && rowData.referenceParticipation && (
                  <Button 
                    icon={rowData.ticketSent ? "pi pi-check" : "pi pi-envelope"} 
                    rounded 
                    severity={rowData.ticketSent ? "success" : "primary"}
                    onClick={() => handleSendTicket(rowData.id)}
                    title={rowData.ticketSent ? 'Ticket déjà envoyé' : 'Envoyer le ticket'}
                    size="small"
                  />
                )}
                {rowData.statut === 'present' && (
                  <Button 
                    icon="pi pi-megaphone" 
                    rounded 
                    severity="help"
                    onClick={() => handleSendWelcomeEmail(rowData.id)}
                    title="Envoyer email de bienvenue"
                    size="small"
                  />
                )}
              </div>
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
        </DataTable>
      </div>
    </div>
  );
}

function EditVisiteurRow({ visiteur, conferences, onSave, onCancel }) {
  const [editData, setEditData] = useState(visiteur);

  return (
    <>
      <div>
        <input
          type="text"
          value={editData.prenom}
          onChange={(e) => setEditData({ ...editData, prenom: e.target.value })}
          placeholder="Prénom"
          style={{ width: '100%', marginBottom: '5px', fontSize: '12px' }}
        />
        <input
          type="text"
          value={editData.nom}
          onChange={(e) => setEditData({ ...editData, nom: e.target.value })}
          placeholder="Nom"
          style={{ width: '100%', fontSize: '12px' }}
        />
      </div>
      <div>
        <input
          type="email"
          value={editData.email}
          onChange={(e) => setEditData({ ...editData, email: e.target.value })}
          style={{ width: '100%', fontSize: '12px' }}
        />
      </div>
      <div>
        <input
          type="text"
          value={editData.telephone}
          onChange={(e) => setEditData({ ...editData, telephone: e.target.value })}
          style={{ width: '100%', fontSize: '12px' }}
        />
      </div>
      <div>
        <input
          type="text"
          value={editData.organisation || ''}
          onChange={(e) => setEditData({ ...editData, organisation: e.target.value })}
          style={{ width: '100%', fontSize: '12px' }}
        />
      </div>
      <div>
        <input
          type="text"
          value={editData.paye || ''}
          onChange={(e) => setEditData({ ...editData, paye: e.target.value })}
          placeholder="Pays"
          style={{ width: '100%', fontSize: '12px' }}
        />
      </div>
      <div>
        <input
          type="text"
          value={editData.ville || ''}
          onChange={(e) => setEditData({ ...editData, ville: e.target.value })}
          placeholder="Ville"
          style={{ width: '100%', fontSize: '12px' }}
        />
      </div>
      <div>
        <select
          value={editData.categorie || ''}
          onChange={(e) => setEditData({ ...editData, categorie: e.target.value })}
          style={{ width: '100%', fontSize: '12px' }}
        >
          <option value="">-- Catégorie --</option>
          <option value="participant">Participant</option>
          <option value="conferencier">Conférencier</option>
          <option value="visiteur">Visiteur</option>
        </select>
      </div>
      <div>
        <select
          value={editData.profil}
          onChange={(e) => setEditData({ ...editData, profil: e.target.value })}
          style={{ width: '100%', fontSize: '12px' }}
        >
          <option value="student">Étudiant</option>
          <option value="professor">Professeur</option>
          <option value="professional">Professionnel</option>
        </select>
      </div>
      <div>
        <select
          value={editData.conference}
          onChange={(e) => setEditData({ ...editData, conference: e.target.value })}
          style={{ width: '100%', fontSize: '12px' }}
        >
          {conferences.map(conf => (
            <option key={conf.id} value={conf.id}>
              {conf.titre}
            </option>
          ))}
        </select>
      </div>
      <div>
        <input
          type="text"
          value={editData.jour_visiteur || ''}
          onChange={(e) => setEditData({ ...editData, jour_visiteur: e.target.value })}
          placeholder="Jours de visite"
          style={{ width: '100%', fontSize: '12px' }}
        />
      </div>
      <div>
        <input
          type="date"
          value={editData.dateInscription}
          onChange={(e) => setEditData({ ...editData, dateInscription: e.target.value })}
          style={{ width: '100%', fontSize: '12px' }}
        />
      </div>
      <div>
        <span style={{ fontSize: '10px', color: '#666' }}>
          {editData.referenceParticipation || 'N/A'}
        </span>
      </div>
      <div>
        <select
          value={editData.statut}
          onChange={(e) => setEditData({ ...editData, statut: e.target.value })}
          style={{ width: '100%', fontSize: '12px' }}
        >
          <option value="en_attente">En attente</option>
          <option value="valide">Validé</option>
          <option value="rejete">Rejeté</option>
          <option value="present">Présent</option>
          <option value="email confirme">Email confirmé</option>
          <option value="email_envoye">Email envoyé</option>
        </select>
      </div>
      <div>
        <textarea
          value={editData.informations || ''}
          onChange={e => setEditData({ ...editData, informations: e.target.value })}
          placeholder="Informations supplémentaires"
          style={{ width: '100%', fontSize: '12px', minHeight: 30 }}
        />
      </div>
      <div className="actions">
        <button onClick={() => onSave(editData)} className="btn-primary" style={{ fontSize: '12px', padding: '6px 12px' }}>
          Sauver
        </button>
        <button onClick={onCancel} className="btn-secondary" style={{ fontSize: '12px', padding: '6px 12px' }}>
          Annuler
        </button>
      </div>
    </>
  );
}

export default VisiteursManagement;
